import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
  Flex,
  HStack,
  Box,
  Divider,
  Avatar,
  Kbd,
} from '@chakra-ui/react'
import { SearchIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import { useState, useMemo, useEffect, useCallback } from 'react'
import type { Location } from '../../../types/location'
import { useLocationSearch } from '../hooks/useLocationSearch'

interface SelectLocationModalProps {
  isOpen: boolean
  locations: Location[]
  prescriberName: string
  title: string
  prescriberNumber: string
  onSelect: (locationId: string) => void
  onJoinNew: () => void
  /** When false, hides the footer link to join another location (e.g. no active org locations left to join). */
  canJoinNewLocation?: boolean
}

function getDefaultLocationId(locations: Location[]): string | null {
  const defaultLocation = locations.find((loc) => loc.isDefault)
  if (defaultLocation) return defaultLocation.id
  return locations.length > 0 ? locations[0].id : null
}

export const SelectLocationModal = ({
  isOpen,
  locations,
  prescriberName,
  title,
  prescriberNumber,
  onSelect,
  onJoinNew,
  canJoinNewLocation = true,
}: SelectLocationModalProps) => {
  const activeLocations = useMemo(() => locations.filter((loc) => loc.status), [locations])
  const { searchQuery, setSearchQuery, filteredLocations } = useLocationSearch(activeLocations)

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)

  useEffect(() => {
    if (activeLocations.length === 0) {
      setSelectedLocationId(null)
      return
    }
    setSelectedLocationId((prev) => {
      if (prev && activeLocations.some((l) => l.id === prev)) return prev
      return getDefaultLocationId(activeLocations)
    })
  }, [activeLocations])

  const selectedLocation = useMemo(() => {
    return activeLocations.find((loc) => loc.id === selectedLocationId) ?? null
  }, [activeLocations, selectedLocationId])

  const handleStartSession = useCallback(() => {
    if (selectedLocationId) {
      onSelect(selectedLocationId)
    }
  }, [selectedLocationId, onSelect])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || filteredLocations.length === 0) return

      const currentIndex = filteredLocations.findIndex((loc) => loc.id === selectedLocationId)

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex = currentIndex < filteredLocations.length - 1 ? currentIndex + 1 : 0
        setSelectedLocationId(filteredLocations[nextIndex].id)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredLocations.length - 1
        setSelectedLocationId(filteredLocations[prevIndex].id)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleStartSession()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, filteredLocations, selectedLocationId, handleStartSession])

  const getInitials = (name: string) => {
    if (name.length >= 2) {
      return `${name[0]}${name[name.length - 1]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      size="lg"
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="2xl" bg="white">
        <Box bg="blue.900" color="white" px={8} py={8}>
          <HStack spacing={4} mb={8}>
            <Avatar
              size="md"
              name={prescriberName}
              bg="blue.500"
              color="white"
              fontWeight="bold"
              getInitials={() => getInitials(prescriberName)}
            />
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="md">
                {title} {prescriberName}
              </Text>
              <Text fontSize="sm" color="gray.400" fontWeight="medium">
                Prescriber {prescriberNumber}
              </Text>
            </VStack>
          </HStack>

          <Text fontSize="xl" fontWeight="bold" mb={2} letterSpacing="tight">
            Where are you working today?
          </Text>
          <Text fontSize="sm" color="gray.400" fontWeight="medium">
            Select your location to start your session
          </Text>
        </Box>

        {/* Body Section */}
        <ModalBody px={8} py={6}>
          <VStack spacing={4} align="stretch">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none" color="gray.400">
                <SearchIcon />
              </InputLeftElement>
              <Input
                placeholder="Search locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="lg"
                borderColor="blue.400"
                borderWidth="2px"
                _hover={{ borderColor: 'blue.500' }}
                _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                fontWeight="medium"
              />
            </InputGroup>

            <Flex
              justify="space-between"
              align="center"
              fontSize="sm"
              color="gray.400"
              fontWeight="medium"
              pt={1}
            >
              <HStack spacing={4}>
                <HStack spacing={1.5}>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                    ↑
                  </Kbd>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                    ↓
                  </Kbd>
                  <Text>navigate</Text>
                </HStack>
                <HStack spacing={1.5}>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs" px={2}>
                    ↵
                  </Kbd>
                  <Text>select</Text>
                </HStack>
              </HStack>
              <Text>
                {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
              </Text>
            </Flex>

            <VStack
              align="stretch"
              spacing={2}
              maxH="220px"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { width: '6px' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'var(--chakra-colors-gray-200)',
                  borderRadius: '24px',
                },
              }}
            >
              {filteredLocations.map((location) => {
                const isSelected = selectedLocationId === location.id
                return (
                  <Flex
                    key={location.id}
                    p={4}
                    borderWidth="1px"
                    borderColor={isSelected ? 'blue.100' : 'gray.200'}
                    borderRadius="lg"
                    bg={isSelected ? 'blue.50' : 'white'}
                    cursor="pointer"
                    onClick={() => setSelectedLocationId(location.id)}
                    align="center"
                    justify="space-between"
                    _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                    transition="all 0.2s"
                  >
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      color={isSelected ? 'blue.600' : 'gray.700'}
                    >
                      {location.name}
                    </Text>
                    <HStack spacing={4}>
                      {location.isDefault && (
                        <Box
                          bg="teal.50"
                          color="teal.700"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          Default
                        </Box>
                      )}
                      <Text fontSize="sm" color="gray.400" fontWeight="medium">
                        {location.suburb}
                      </Text>
                      <Box
                        w={5}
                        h={5}
                        borderRadius="full"
                        borderWidth="2px"
                        borderColor={isSelected ? 'blue.400' : 'gray.300'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={isSelected ? 'blue.400' : 'transparent'}
                      >
                        {isSelected && <Box w={2} h={2} borderRadius="full" bg="white" />}
                      </Box>
                    </HStack>
                  </Flex>
                )
              })}
            </VStack>
          </VStack>
        </ModalBody>

        {/* Footer Section */}
        <Box px={8} pb={6}>
          <Divider borderColor="gray.200" mb={6} />

          <VStack align="stretch" spacing={4} mb={6}>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="wider">
                SELECTED
              </Text>
              <Text fontWeight="bold" fontSize="lg" color="gray.900">
                {selectedLocation ? selectedLocation.name : 'None'}
              </Text>
            </VStack>
            <Flex justify="center">
              <Button
                colorScheme="blue"
                size="md"
                onClick={handleStartSession}
                isDisabled={!selectedLocation}
                rightIcon={<ArrowForwardIcon />}
                px={8}
                borderRadius="lg"
                fontWeight="bold"
                width="full"
              >
                Start session
              </Button>
            </Flex>
          </VStack>

          {canJoinNewLocation ? (
            <>
              <Divider borderColor="gray.200" mb={4} />

              <Text fontSize="sm" color="gray.500" textAlign="center" fontWeight="medium">
                Can't find your location?{' '}
                <Button
                  variant="link"
                  color="blue.500"
                  onClick={onJoinNew}
                  fontWeight="bold"
                  fontSize="sm"
                >
                  Join a new location
                </Button>
              </Text>
            </>
          ) : null}
        </Box>
      </ModalContent>
    </Modal>
  )
}
